import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsamcoAuthService, AsamcoUserService } from '@asamco/base';
import { pathJoin, showErrorSwal } from '@asamco/utils';
import { DxLookupComponent, DxLookupModule } from 'devextreme-angular';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ApplicationLogin } from '../../interfaces/application-login.interface';
import { Link } from '../../interfaces/link.interface';
import { MarketPlaceItem } from '../../interfaces/market-place-item.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { ApplicationLoginService } from '../../services/application-login.service';
import { LinkEditApplicationLoginComponent } from '../link-edit-application-login/link-edit-application-login.component';
import { LinkEditAuthGenericModalComponent } from '../link-edit-setup/link-edit-auth-generic-modal/link-edit-auth-generic-modal.component';
import { LinkService } from '../../services/link.service';
import { DxTemplateModule } from 'devextreme-angular/core';
import { AsLet } from '../../../../../../base/src/lib/as-let/as-let.directive';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';
import { ApplicationParams } from '../../interfaces/application-login-params.interface';
import { ApplicationLoginParamsService } from '../../services/application-login-params.service';

@Component({
	selector: 'al-link-edit-login-details',
	templateUrl: './link-edit-login-details.component.html',
	styleUrls: ['./link-edit-login-details.component.scss'],
	standalone: true,
	imports: [
		NgIf,
		AsLet,
		DxLookupModule,
		DxTemplateModule,
		NgClass,
		LinkEditApplicationLoginComponent,
		FormsModule,
		ReactiveFormsModule,
		LinkEditAuthGenericModalComponent,
		AsyncPipe,
	],
})
export class LinkEditLoginDetailsComponent implements OnInit, OnDestroy {
	@ViewChild('editAppFieldModal') editAppFields: LinkEditAuthGenericModalComponent;

	@Input()
	formGroup2: FormGroup;

	@Input()
	link: Link;

	@Input()
	linkInfo: MarketPlaceItem;

	@Input()
	fromAppName: string;

	@Input()
	toAppName: string;

	@ViewChild('fromApp')
	fromApp: LinkEditApplicationLoginComponent;

	@ViewChild('toApp')
	toApp: LinkEditApplicationLoginComponent;

	@ViewChild('fromAppLookup')
	fromAppLookup: DxLookupComponent;

	@ViewChild('toAppLookup')
	toAppLookup: DxLookupComponent;

	private destroy$ = new Subject<void>();

	public fromNewLogin: boolean = false;
	public toNewLogin: boolean = false;

	public showFromLoginFormSubject: Subject<boolean> = new BehaviorSubject<boolean>(false);
	public showFromLoginForm$ = this.showFromLoginFormSubject.asObservable();

	public showToLoginFormSubject: Subject<boolean> = new BehaviorSubject<boolean>(false);
	public showToLoginForm$ = this.showToLoginFormSubject.asObservable();

	isAdmin: boolean = false;

	public fromApplicationLoginsSubject: Subject<ApplicationLogin[]> = new BehaviorSubject<ApplicationLogin[]>(undefined);
	public toApplicationLoginsSubject: Subject<ApplicationLogin[]> = new BehaviorSubject<ApplicationLogin[]>(undefined);

	public fromApplicationLogins$: Observable<ApplicationLogin[]> = this.fromApplicationLoginsSubject.asObservable();
	public toApplicationLogins$: Observable<ApplicationLogin[]> = this.toApplicationLoginsSubject.asObservable();

	public selectedFromApplicationLogin: BehaviorSubject<ApplicationLogin> = new BehaviorSubject<ApplicationLogin>(undefined);
	public selectedToApplicationLogin: BehaviorSubject<ApplicationLogin> = new BehaviorSubject<ApplicationLogin>(undefined);

	public selectedFromApplicationLogin$ = this.selectedFromApplicationLogin.asObservable();
	public selectedToApplicationLogin$ = this.selectedToApplicationLogin.asObservable();

	public fromApplicationLoginParamsSubject: Subject<ApplicationParams> = new BehaviorSubject<ApplicationParams>(undefined);
	public toApplicationLoginParamsSubject: Subject<ApplicationParams> = new BehaviorSubject<ApplicationParams>(undefined);

	public fromApplicationLoginParams$: Observable<ApplicationParams> = this.fromApplicationLoginParamsSubject.asObservable();
	public toApplicationLoginParams$: Observable<ApplicationParams> = this.toApplicationLoginParamsSubject.asObservable();

	get fromLoginContext() {
		return combineLatest([this.fromApplicationLogins$, this.selectedFromApplicationLogin$, this.fromApplicationLoginParams$]).pipe(
			map(res => {
				// login not selectable for inty users (automatic creation via messagebus) or any applogins without parameters other than name
				const loginSelectable = !this.fromAppName.toLocaleLowerCase().includes('inty') && (res[2]?.oAuth || res[2]?.parameters.some(p => p.fieldName.toLowerCase() !== 'name'));
				return loginSelectable ? {fromApplicationLogins: res[0], data: res[1], fromApplicationLoginParams: res[2]} : null;
			})
		);
	}

	get toLoginContext() {
		return combineLatest([this.toApplicationLogins$, this.selectedToApplicationLogin$, this.toApplicationLoginParams$]).pipe(
			map(res => {
				const loginSelectable = res[2]?.oAuth || res[2]?.parameters.some(p => p.fieldName.toLowerCase() !== 'name');
				return loginSelectable ? {toApplicationLogins: res[0], data: res[1], toApplicationLoginParams: res[2]} : null;
			})
		);
	}

	currentCompanyId: number;

	public loadedLogins: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	formGroup: FormGroup = new FormGroup({});

	constructor(
		private auth: AsamcoAuthService,
		private asamcoUserService: AsamcoUserService,
		private selectedCompanyService: SelectedCompanyService,
		private httpClient: HttpClient,
		private linkService: LinkService,
		private applicationLoginService: ApplicationLoginService,
		private cdr: ChangeDetectorRef,
		private appLoginParamsService: ApplicationLoginParamsService
	) { }

	ngOnInit(): void {
		this.currentCompanyId = this.selectedCompanyService.getSelectedCompanyID();

		this.asamcoUserService.isAdmin().then(result => (this.isAdmin = result));
		this.initForm();

		this.appLoginParamsService.getParams(this.linkInfo.fromApplicationId).subscribe(ap => this.fromApplicationLoginParamsSubject.next(ap));
		this.appLoginParamsService.getParams(this.linkInfo.toApplicationId).subscribe(ap => this.toApplicationLoginParamsSubject.next(ap));
	}

	setupSelectedItems() {
		this.setupSelectedFromItems(true, false, false);
		this.setupSelectedToItems(true, false, false);

		this.loadedLogins.next(true);
	}

	setupSelectedFromItems(overwriteCurrentLogin: boolean, isCurrentlySelectedLogin: boolean, clearFields: boolean) {
		this.applicationLoginService
			.getApplicationLoginsByApplicationId(this.linkInfo.fromApplicationId, this.currentCompanyId)
			.pipe(
				map((resp: any) => {
					this.fromApplicationLoginsSubject.next(resp);
					if (isCurrentlySelectedLogin) {
						this.fromNewLogin = false;
						this.fromAppLookup.setDisabledState(this.fromNewLogin);
						if (this.fromNewLogin) this.fromAppLookup.writeValue(null);
						this.showFromLoginFormSubject.next(this.fromNewLogin);
						this.selectedFromApplicationLogin.next(null);
						this.fromApp.setLogin(null);
					}
					if (overwriteCurrentLogin) {
						let foundFromitem = resp.filter((e: ApplicationLogin) => e.id === this.link.fromApplicationLoginId)[0];
						this.selectedFromApplicationLogin.next(foundFromitem);
					} else if (clearFields) {
						//Or reset the fields
						this.toApp.setLogin(null);
					}
				}, takeUntil(this.destroy$))
			)
			.subscribe({error: showErrorSwal});
	}

	setupSelectedToItems(overwriteCurrentLogin: boolean, isCurrentlySelectedLogin: boolean, clearFields: boolean) {
		this.applicationLoginService
			.getApplicationLoginsByApplicationId(this.linkInfo.toApplicationId, this.currentCompanyId)
			.pipe(
				map((resp: any) => {
					this.toApplicationLoginsSubject.next(resp);
					if (isCurrentlySelectedLogin) {
						this.toNewLogin = false;
						this.toAppLookup.setDisabledState(this.toNewLogin);
						if (this.toNewLogin) this.toAppLookup.writeValue(null);
						this.showToLoginFormSubject.next(this.toNewLogin);
						this.selectedToApplicationLogin.next(null);
						this.toApp.setLogin(null);
						//Clear fields are deletion
					}

					if (overwriteCurrentLogin) {
						let foundToItem = resp.filter((e: ApplicationLogin) => e.id === this.link.toApplicationLoginId)[0];
						this.selectedToApplicationLogin.next(foundToItem);
					} else if (clearFields) {
						//Or reset the fields
						this.toApp.setLogin(null);
					}
				}, takeUntil(this.destroy$))
			)
			.subscribe({error: showErrorSwal});
	}

	reloadSelectedItem(appSide: string, overwrite: boolean, isSelectedLogin: boolean, clearFields: boolean): void {
		switch (appSide) {
			case 'from':
				this.setupSelectedFromItems(overwrite, isSelectedLogin, clearFields);
				break;
			case 'to':
				this.setupSelectedToItems(overwrite, isSelectedLogin, clearFields);
				break;
		}
	}

	private initForm() {
		this.linkService.get(this.link.id).subscribe(e => {
			this.formGroup.addControl(
				'from',
				new FormGroup({ id: new FormControl(null), applicationId: new FormControl(this.linkInfo.fromApplicationId) })
			);
			this.formGroup.addControl(
				'to',
				new FormGroup({ id: new FormControl(null), applicationId: new FormControl(this.linkInfo.toApplicationId) })
			);
			this.link = e;
			this.setupSelectedItems();
		});
	}

	/** Gets and combines the results and returns null if one is invalid */
	public save() {
		const fromResult = this.fromApp.save();

		const toResult = this.toApp.save();

		if (toResult !== null && fromResult !== null) {
			return {
				fromApplicationLoginId: fromResult,
				toApplicationLoginId: toResult,
			};
		}

		return null;
	}

	public toggleFromLogin(e) {
		this.fromNewLogin = !this.fromNewLogin;
		this.fromAppLookup.setDisabledState(this.fromNewLogin);
		if (this.fromNewLogin) this.fromAppLookup.writeValue(null);
		this.showFromLoginFormSubject.next(this.fromNewLogin);
	}

	public toggleToLogin(e) {
		this.toNewLogin = !this.toNewLogin;
		this.toAppLookup.setDisabledState(this.toNewLogin);
		if (this.toNewLogin) this.toAppLookup.writeValue(null);
		this.showToLoginFormSubject.next(this.toNewLogin);
	}

	public valueChanged(event, part: string) {
		switch (part) {
			case 'from':
				if(!event.event && !this.fromNewLogin) return;
				this.fromApp.setLogin(event.value);
				this.selectedFromApplicationLogin.next(event.value);
				if (event.value === null && !this.fromNewLogin) this.showFromLoginFormSubject.next(false);
				else this.showFromLoginFormSubject.next(true);
				break;
			case 'to':
				if(!event.event && !this.toNewLogin) return;
				this.toApp.setLogin(event.value);
				this.selectedToApplicationLogin.next(event.value);
				if (event.value === null && !this.toNewLogin) this.showToLoginFormSubject.next(false);
				else this.showToLoginFormSubject.next(true);
				break;
		}
	}

	deleteApplicationLoginBtn(event, item: ApplicationLogin, appSide: string) {
		if (event?.stopPropagation) {
			event.stopPropagation();
		}

		//Check if current login is active on any other links
		this.applicationLoginService.checkIfLoginIsUsed(item.id).subscribe((loginIsBeingUsed: Boolean) => {
			let text = 'Are you sure you want to delete the following login: ' + item.name;
			if (loginIsBeingUsed) {
				text = `The application login you are about to delete (${item.name}) has been assigned to one or more of your integration tiles.`;
			}

			Swal.fire({
				icon: 'warning',
				title: 'Are you sure?',
				text: text,
				showCancelButton: true,
				confirmButtonColor: '#dd6b55',
				cancelButtonColor: '#999',
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel',
			}).then((result) => {
				if (result.value && !result.dismiss) {
					this.applicationLoginService.deleteApplicationLogin(item.id).subscribe({
						next: () => {
							Swal.fire({
								icon: 'success',
								text: 'Successfully deleted login entry',
							});

							let shouldCloseField: boolean = false;
							let shouldClearField: boolean = false;

							if (appSide === 'from' && this.selectedFromApplicationLogin.value?.id === item.id) {
								shouldCloseField = true;
								shouldClearField = true;
							} else if (appSide === 'to' && this.selectedToApplicationLogin.value?.id === item.id) {
								shouldCloseField = true;
								shouldClearField = true;
							}

							this.reloadSelectedItem(appSide, false, shouldCloseField, shouldClearField);
						},
						error: showErrorSwal
					});
				}
			});
		});
	}

	openAppLoginManagementModal() {
		this.editAppFields.showModal();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
